import { memo, useRef, useEffect, useMemo, useState, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Paper, Typography, Box, CircularProgress, Checkbox, Chip, Link, IconButton, Tooltip,
} from '@mui/material';
import { FixedSizeList, ListOnScrollProps } from 'react-window';
import { AccessTimeOutlined as ClockIcon } from '@mui/icons-material';
import OrderDetailDialog from '../../../app/components/OrderDetailDialog';
import OrderLogDialog from '../../../app/components/OrderLogDialog';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import NO05 from '../../../models/NO05';
import * as actionsNO05 from '../../../store/actions/indexNO05';
import { STATUS_GROUPS, StatusGroup, statusInGroup } from '../no05StatusGroups';
import { getVisibleOrders } from '../no05VisibleOrders';
import * as APICalls from '../../../felles/APICalls_NO05';
import CSharpUtils from '../../../felles/CSharpUtils';
import Utils from '../../../felles/Utils';

const FALLBACK_GROUP: StatusGroup = { name: 'Ukjent', color: '#9e9e9e', bg: '#fafafa', border: '#9e9e9e', isReturn: false, statuses: null };

const getOrderGroup = (status: string, ordreNr: string): StatusGroup => {
    if (ordreNr.startsWith('9'))
        return STATUS_GROUPS.find(g => g.isReturn) ?? FALLBACK_GROUP;
    return STATUS_GROUPS.find(g => statusInGroup(g, status)) ?? FALLBACK_GROUP;
};

// Row-chip color only — independent of which card a status counts toward (see no05StatusGroups.ts:
// these count toward "Printet" to avoid a duplicate box, but are still waiting/in-progress pick
// orders, not actually printed, so the chip itself stays yellow like "Venteordre").
const CHIP_COLOR_OVERRIDE: Record<string, { border: string; color: string; bg: string }> = {
    'Vente / plukkordre VDA': { border: '#f9a825', color: '#f9a825', bg: '#fffde7' },
    'Venteordre/plukkordre':  { border: '#f9a825', color: '#f9a825', bg: '#fffde7' },
};

const getChipStyle = (status: string, group: StatusGroup) => CHIP_COLOR_OVERRIDE[status] ?? group;

// Pure, stateless helpers — hoisted out of the component so they're not recreated (and don't
// break row memoization) on every render.
const getTrackingUrl = (trackingNumber: string, carrierCode: string): string => {
    const tn = trackingNumber.trim();
    const cc = carrierCode.trim().toUpperCase();
    if (cc === 'NC' || cc === 'LF')
        return `http://amphora.norcargo.no/track/trackntrace/simple_fraktbrevframeset.jsp?count=1&intern=ja&fraktbrevnr=${tn}`;
    if (cc === 'LG')
        return `http://myschenker.linjegods.no/DavDB/srv_spor_kriterier.html?fraktnr=${tn}&lang=nor`;
    if (cc === 'EK')
        return `https://ektransport.opter.cloud/tt/?keyword=${encodeURIComponent(tn)}`;
    if (cc === 'TP')
        return `https://www.postnord.no/pakkesporing?shipmentId=${tn}`;
    if (cc === 'RA')
        return `/system/system/Sped_ramberg.php?sndnr=${tn}`;
    return `https://sporing.bring.no/sporing/${tn}`;
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatTime = (tid: string) => {
    if (!tid) return '';
    const t = tid.toString().padStart(6, '0');
    return `${t.substring(0, 2)}:${t.substring(2, 4)}`;
};

const formatAmount = (amount: number) =>
    new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const formatPct = (pct: number) => {
    if (pct === 0 || pct == null) return null;
    return `${new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(pct)}%`;
};

const isRetur = (order: NO05) => order.ordreNr.startsWith('9');

// Fixed (not content-based) column widths. The list below is virtualized — only a scrolled
// "window" of rows exists in the DOM at any moment — so native table auto-sizing (column widths
// derived from currently-visible rows' content) would make columns visibly jump while scrolling.
// Floor (minimum) pixel widths — the narrow-screen/short-content guarantee for each column.
const COL = {
    checkbox: 40, ordre: 190, kunde: 110, navn: 200, rekv: 110, status: 170,
    om: 56, bestlev: 130, belop: 90, pct: 60, log: 40,
};
// Target share of the row's width on a screen wide enough to need it — measured directly off a
// real NO04 screenshot (column pixel boundaries divided by the row's total width), so wide-screen
// NO05 scales proportionally the same way NO04's native table (auto layout, width:100%) actually
// does, instead of guessed flex weights. Every column is `minmax(floor, target%)`, so on a narrow
// screen the floor above wins and columns stay readable; on a wide screen each grows toward its
// measured NO04 share. Row-to-row content must not shift column widths here (unlike NO04's plain
// table), since react-window only measures/renders the rows currently scrolled into view — hence
// minmax floors instead of pure content-based sizing.
const SHARE = {
    checkbox: '1.8%', ordre: '16%', kunde: '10%', navn: '17%', rekv: '10.2%', status: '13.2%',
    om: '6.4%', bestlev: '9.8%', belop: '7%', pct: '7%', log: '1.8%',
};
const GRID_TEMPLATE = Object.keys(COL)
    .map((key) => `minmax(${COL[key as keyof typeof COL]}px, ${SHARE[key as keyof typeof SHARE]})`)
    .join(' ');
// One fixed height for every row (react-window's FixedSizeList requires a constant item size).
// Rows without a secondary line (Faktura/Frakt-Sporing) just carry extra whitespace instead.
const ROW_HEIGHT = 56;

const useStyles = makeStyles()((theme) => ({
    container: {
        margin: 0, borderRadius: 0, boxShadow: 'none', borderTop: '1px solid #e0e0e0',
        flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0,
    },
    emptyState: { padding: theme.spacing(8), textAlign: 'center', color: theme.palette.text.secondary },
    loadingContainer: { padding: theme.spacing(8), display: 'flex', justifyContent: 'center' },
    dataRow: {
        display: 'grid', gridTemplateColumns: GRID_TEMPLATE, alignItems: 'center', boxSizing: 'border-box' as const,
        cursor: 'pointer', borderBottom: '1px solid #e8e8e8', backgroundColor: '#fff',
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    dataRowRetur: {
        display: 'grid', gridTemplateColumns: GRID_TEMPLATE, alignItems: 'center', boxSizing: 'border-box' as const,
        cursor: 'pointer', borderBottom: '1px solid #e8e8e8', backgroundColor: '#fff6f6',
        '&:hover': { backgroundColor: '#ffe8e8' },
    },
    cell: { fontSize: '0.82rem', padding: '4px 10px', overflow: 'hidden', minWidth: 0 },
    truncate: { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' as const },
    ordreLink: {
        color: theme.palette.primary.main, fontWeight: 700, fontSize: '0.88rem',
        cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
    },
    dateTime: { color: theme.palette.text.secondary, fontSize: '0.78rem', marginLeft: 6 },
    label: { color: theme.palette.text.secondary, fontSize: '0.78rem', marginRight: 2 },
    value: { fontSize: '0.82rem', fontWeight: 500 },
    negative: { color: theme.palette.error.main, fontWeight: 600 },
    positive: { color: theme.palette.text.primary, fontWeight: 500 },
    pct: { fontSize: '0.78rem', fontWeight: 600, minWidth: 52, textAlign: 'right' as const },
    loadMoreContainer: { padding: theme.spacing(2), display: 'flex', justifyContent: 'center' },
}));

type Classes = ReturnType<typeof useStyles>['classes'];

interface RowData {
    orders: NO05[];
    selected: Set<string>;
    classes: Classes;
    onSelectRow: (ordreNr: string, checked: boolean) => void;
    onOrdreNrClick: (ordreNr: string) => void;
    onFraktClick: (faktura: string) => void;
    onOrderLogClick: (order: NO05) => void;
}

interface RowProps {
    index: number;
    style: CSSProperties;
    data: RowData;
}

// Rendered by react-window for only the rows currently scrolled into view (plus a small
// overscan buffer) — regardless of how many orders are in the filtered list, so switching
// status cards on a list of thousands no longer mounts/unmounts thousands of rows at once.
const NO05Row = memo(function NO05Row({ index, style, data }: RowProps) {
    const { orders, selected, classes, onSelectRow, onOrdreNrClick, onFraktClick, onOrderLogClick } = data;
    const order = orders[index];
    const isSelected = selected.has(order.ordreNr);
    const pct = formatPct(order.brfProsent);
    const g = getOrderGroup(order.status, order.ordreNr);
    const chipStyle = getChipStyle(order.status, g);
    // Retur keeps its deliberate "Retur" override label regardless of raw status; every other
    // row shows its own actual status (matches NO04). Hardcoded rather than g.name, since g.name
    // is the cross-status box's display name (currently "Kredit") and must be free to change
    // independently of what this table column shows per row.
    const statusLabel = isRetur(order) ? 'Retur' : order.status;

    return (
        <div role="row" style={style} className={isRetur(order) ? classes.dataRowRetur : classes.dataRow}>
            <div role="cell" className={classes.cell} style={{ paddingLeft: 8 }}>
                <Checkbox size="small" checked={isSelected} onChange={(e) => onSelectRow(order.ordreNr, e.target.checked)} />
            </div>

            <div role="cell" className={classes.cell}>
                <Box display="flex" alignItems="baseline" gap={0.5}>
                    <Link className={classes.ordreLink} underline="hover" onClick={() => onOrdreNrClick(order.ordreNr)} sx={{ cursor: 'pointer' }}>
                        {order.ordreNr}
                    </Link>
                    <span className={classes.dateTime}>
                        {formatDate(order.dato)}{order.tid ? ` ${formatTime(order.tid)}` : ''}
                    </span>
                </Box>
                {order.faktura && (
                    <Box className={classes.truncate} sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3 }}>
                        <span style={{ marginRight: 2 }}>Faktura:</span>
                        <Link underline="hover" sx={{ fontSize: '0.75rem', cursor: 'pointer' }} onClick={() => onFraktClick(order.faktura)}>
                            {order.faktura}
                        </Link>
                    </Box>
                )}
            </div>

            <div role="cell" className={classes.cell}>
                <span className={classes.label}>Kunde:</span>
                <span className={classes.value}>{order.kundenr}</span>
            </div>

            <div role="cell" className={classes.cell}>
                <Box className={classes.truncate} sx={{ fontSize: '0.82rem', fontWeight: 500 }} title={order.navn}>{order.navn}</Box>
                {order.fraktEdi && (
                    <Box className={classes.truncate} sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3 }} title={order.fraktEdi}>
                        {order.speditor && (
                            <span style={{ marginRight: 4 }}>{order.speditor} –</span>
                        )}
                        Frakt/Sporing:{' '}
                        {order.speditor ? (
                            <Link
                                href={getTrackingUrl(order.fraktEdi, order.speditor)}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ fontSize: '0.75rem' }}
                            >
                                {order.fraktEdi}
                            </Link>
                        ) : (
                            order.fraktEdi
                        )}
                    </Box>
                )}
            </div>

            <div role="cell" className={classes.cell}>
                <span className={classes.label}>Rekv:</span>
                <span className={classes.value}>{order.rekvNr}</span>
            </div>

            <div role="cell" className={classes.cell}>
                <Chip
                    label={statusLabel}
                    size="small"
                    variant="outlined"
                    sx={{
                        fontSize: '0.72rem', height: 22, maxWidth: 164,
                        borderColor: chipStyle.border, color: chipStyle.color, backgroundColor: chipStyle.bg, fontWeight: 600,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                />
            </div>

            <div role="cell" className={classes.cell}>
                <span className={classes.label}>OM:</span>
                <span className={classes.value}>{order.om}</span>
            </div>

            <div role="cell" className={classes.cell} style={{ whiteSpace: 'nowrap' }}>
                <span className={classes.label}>Best:</span>
                <span className={classes.value}>{Utils.round2(order.antBestilt)}</span>
                {'  '}
                <span className={classes.label}>Lev:</span>
                <span className={classes.value} style={{ color: order.antLevert > 0 ? '#1976d2' : undefined }}>{Utils.round2(order.antLevert)}</span>
            </div>

            <div role="cell" className={classes.cell} style={{ textAlign: 'right' }}>
                <span className={order.belop < 0 ? classes.negative : classes.positive}>{formatAmount(order.belop)} kr</span>
            </div>

            <div role="cell" className={classes.cell} style={{ textAlign: 'right' }}>
                {pct && <span className={classes.pct} style={{ color: order.brfProsent < 0 ? '#d32f2f' : undefined }}>{pct}</span>}
            </div>

            <div role="cell" className={classes.cell} style={{ textAlign: 'right' }}>
                <Tooltip title="Ordrelogg">
                    <IconButton size="small" onClick={() => onOrderLogClick(order)}>
                        <ClockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );
});

// Measures a wrapper element's height via ResizeObserver — FixedSizeList needs a concrete
// numeric height, and this container's actual height depends on flex layout (status cards,
// search box, etc. above it), not a fixed value we can hardcode.
// A callback ref (not a ref object + useLayoutEffect) is required here: the element carrying
// this ref only exists once loading/error/empty-state early-returns are past, so a one-shot
// effect (empty deps) can run before that element ever mounts and never re-attach — leaving
// height stuck at 0 forever. A callback ref re-fires exactly when the node actually (dis)appears.
function useMeasuredHeight<T extends HTMLElement>() {
    const [height, setHeight] = useState(0);
    const observerRef = useRef<ResizeObserver | null>(null);
    const ref = useCallback((el: T | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) setHeight(entry.contentRect.height);
        });
        observer.observe(el);
        observerRef.current = observer;
    }, []);
    return [ref, height] as const;
}

function NO05List() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const listRef = useRef<FixedSizeList>(null);
    const isFetchingRef = useRef(false);
    const [listWrapperRef, listHeight] = useMeasuredHeight<HTMLDivElement>();

    const allOrders = useSelector((state: AppState) => state.NO05AapneOrdre.orders);
    const selectedStatus = useSelector((state: AppState) => state.NO05AapneOrdre.selectedStatus);
    const selectedDate = useSelector((state: AppState) => state.NO05AapneOrdre.selectedDate);
    const deliveryFilter = useSelector((state: AppState) => state.NO05AapneOrdre.deliveryFilter);
    const loading = useSelector((state: AppState) => state.NO05AapneOrdre.loading);
    const loadingMore = useSelector((state: AppState) => state.NO05AapneOrdre.loadingMore);
    const hasMore = useSelector((state: AppState) => state.NO05AapneOrdre.hasMore);
    const currentPage = useSelector((state: AppState) => state.NO05AapneOrdre.currentPage);
    const lastSearchParams = useSelector((state: AppState) => state.NO05AapneOrdre.lastSearchParams);
    const error = useSelector((state: AppState) => state.NO05AapneOrdre.error);
    const orderDetail = useSelector((state: AppState) => state.NO05AapneOrdre.orderDetail);
    const orderDetailLoading = useSelector((state: AppState) => state.NO05AapneOrdre.orderDetailLoading);
    const selectedOrderNrs = useSelector((state: AppState) => state.NO05AapneOrdre.selectedOrderNrs);

    const selected = useMemo(() => new Set(selectedOrderNrs), [selectedOrderNrs]);
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    const [dialogOpen, setDialogOpen] = useState(false);

    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [logLoading, setLogLoading] = useState(false);
    const [logEntries, setLogEntries] = useState<any[]>([]);
    const [logOrder, setLogOrder] = useState<NO05 | null>(null);

    const displayOrders = useMemo(
        () => getVisibleOrders(allOrders, selectedStatus, selectedDate, deliveryFilter),
        [allOrders, selectedStatus, selectedDate, deliveryFilter],
    );

    // Jump back to the top of the list whenever the filtered set changes (switching status
    // cards) — otherwise the virtualized list can be left scrolled past the end of a shorter list.
    useEffect(() => {
        listRef.current?.scrollTo(0);
    }, [selectedStatus, selectedDate, deliveryFilter]);

    // Stable callback refs — kept identity-stable across renders (deps never change) so that
    // NO05Row's memoization isn't defeated by a "new" handler prop on every parent render.
    const handleFraktClick = useCallback(async (faktura: string) => {
        try {
            const companyId = CSharpUtils.T_companyID;
            const response = await APICalls.getInvoicePDF(companyId, faktura);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch {
            alert('Kunne ikke laste faktura PDF. Vennligst prøv igjen.');
        }
    }, []);

    const handleOrdreNrClick = useCallback((ordreNr: string) => {
        dispatch(actionsNO05.NO05OrderDetailRequest(CSharpUtils.T_companyID, ordreNr));
        setDialogOpen(true);
    }, [dispatch]);

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch(actionsNO05.NO05OrderDetailClear());
    };

    const handleOrderLogClick = useCallback(async (order: NO05) => {
        setLogOrder(order);
        setLogDialogOpen(true);
        setLogLoading(true);
        try {
            const res = await APICalls.fetchOrderLog(CSharpUtils.T_companyID, order.ordreNr);
            setLogEntries(res.data?.entries ?? []);
        } catch {
            setLogEntries([]);
        } finally {
            setLogLoading(false);
        }
    }, []);

    const handleLogDialogClose = () => {
        setLogDialogOpen(false);
        setLogEntries([]);
        setLogOrder(null);
    };

    const handleSelectRow = useCallback((ordreNr: string, checked: boolean) => {
        const next = new Set(selectedRef.current);
        checked ? next.add(ordreNr) : next.delete(ordreNr);
        dispatch(actionsNO05.NO05SetSelected([...next]));
    }, [dispatch]);

    const itemData = useMemo<RowData>(() => ({
        orders: displayOrders,
        selected,
        classes,
        onSelectRow: handleSelectRow,
        onOrdreNrClick: handleOrdreNrClick,
        onFraktClick: handleFraktClick,
        onOrderLogClick: handleOrderLogClick,
    }), [displayOrders, selected, classes, handleSelectRow, handleOrdreNrClick, handleFraktClick, handleOrderLogClick]);

    useEffect(() => { isFetchingRef.current = loadingMore; }, [loadingMore]);

    // Infinite scroll — driven by react-window's own onScroll instead of a manual DOM listener,
    // since the list now owns its own scroll container.
    const handleListScroll = useCallback(({ scrollDirection, scrollOffset }: ListOnScrollProps) => {
        if (scrollDirection !== 'forward' || isFetchingRef.current || !hasMore || !lastSearchParams || listHeight === 0) return;
        const totalHeight = displayOrders.length * ROW_HEIGHT;
        if (totalHeight - scrollOffset - listHeight < 150) {
            isFetchingRef.current = true;
            dispatch(actionsNO05.NO05LoadMoreRequest({ ...lastSearchParams, page: currentPage + 1, pageSize: 100 }));
        }
    }, [hasMore, lastSearchParams, currentPage, dispatch, displayOrders.length, listHeight]);

    // No search performed yet — don't show the list at all
    if (!lastSearchParams) return null;

    if (loading) {
        return <Paper className={classes.container}><Box className={classes.loadingContainer}><CircularProgress /></Box></Paper>;
    }

    if (error) {
        return <Paper className={classes.container}><Box className={classes.emptyState}><Typography color="error">{error}</Typography></Box></Paper>;
    }

    if (displayOrders.length === 0) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.emptyState}>
                    <Typography variant="h6">Ingen ordre funnet</Typography>
                    <Typography variant="body2">Prøv å endre søkekriteriene eller velg en annen status</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper className={classes.container}>
            {/* Virtualized list — only rows scrolled into view are ever mounted */}
            <Box ref={listWrapperRef} role="table" sx={{ flex: 1, minHeight: 0 }}>
                {listHeight > 0 && (
                    <FixedSizeList
                        ref={listRef}
                        height={listHeight}
                        width="100%"
                        itemCount={displayOrders.length}
                        itemSize={ROW_HEIGHT}
                        itemData={itemData}
                        itemKey={(index, data) => data.orders[index].ordreNr}
                        onScroll={handleListScroll}
                        overscanCount={8}
                    >
                        {NO05Row}
                    </FixedSizeList>
                )}
            </Box>

            {loadingMore && <Box className={classes.loadMoreContainer}><CircularProgress size={24} /></Box>}

            {/* Order Detail Dialog */}
            <OrderDetailDialog open={dialogOpen} loading={orderDetailLoading} orderDetail={orderDetail} onClose={handleDialogClose} />

            {/* Order Log Dialog */}
            <OrderLogDialog
                open={logDialogOpen}
                loading={logLoading}
                orderNumber={logOrder?.ordreNr ?? ''}
                customerName={logOrder?.navn}
                customerNumber={logOrder?.kundenr}
                entries={logEntries}
                onClose={handleLogDialogClose}
            />
        </Paper>
    );
}

// No props — memo means a parent re-render for unrelated reasons (e.g. toggling "på tvers av
// status" in NO05Page) doesn't force this whole list (and its potentially large row set) to
// re-render; it still re-renders on its own Redux-driven state changes via useSelector.
export default memo(NO05List);
